// Pure types/constants for the Phase 3 permission system — deliberately has
// NO imports from anything server-only (no next/headers, no Supabase server
// client). Safe to import from client components. Server-only logic
// (computeEffectivePermissions, requirePermission, requireTopRole,
// requireDestructiveRole) lives in src/lib/permissions.ts instead, which
// imports these types rather than declaring them, so a client component
// pulling in a real value (e.g. CAPABILITY_KEYS) never drags next/headers
// into the client bundle.

// Capability keys must match the `permissions` jsonb keys seeded in
// supabase/migrations/020_staff_titles_and_permissions.sql. Kept in sync by
// convention, not enforced — see that migration's header comment and
// CLAUDE.md's "Shared Constants" note (this project doesn't yet have a
// mechanism to share constants between SQL and TS).
export const CAPABILITY_KEYS = [
  'checkout_checkin',
  'approve_requests',
  'view_request_queue',
  'edit_inventory',
  'run_audits',
  'view_repair_log',
  'edit_repair_log',
  'manage_inventory',
  'manage_students',
  'manage_staff_accounts',
  'access_kiosk_tv',
  'admin_configurable_lists',
  'admin_subcategories',
  'create_tag_vocab',
  'create_pool',
  'admin_kiosk_manager',
  'admin_checkout_windows',
  'admin_packages_manager',
  'admin_addon_pool',
  'admin_project_type_defaults',
  'change_item_role',
  'edit_barcode',
  'edit_po_number',
  'view_user_action_metadata',
  'manage_permissions',
] as const;

export type CapabilityKey = (typeof CAPABILITY_KEYS)[number];

/**
 * Notification preference keys — deliberately NOT part of CAPABILITY_KEYS.
 * Capabilities gate what you're allowed to do (default OFF, top roles bypass
 * the title/override system entirely and get everything). Notification
 * preferences gate what you're told about (default ON — opt-out, not
 * opt-in — and top roles go through the same title/override merge as
 * everyone else, since "can approve requests" and "wants a ping every time
 * one comes in" are genuinely different questions with different answers).
 * Stored in the same staff_titles.permissions / staff_permission_overrides.permissions
 * jsonb columns as capabilities (schemaless, no migration needed) — kept in
 * a separate constant/type only so the two concepts can't be confused at
 * the call site.
 */
export const NOTIFICATION_KEYS = [
  'notify_checkout_submitted',
  'notify_checkin_submitted',
  'notify_request_submitted',
  'notify_repair_events',
  'notify_inventory_updates',
] as const;

export type NotificationKey = (typeof NOTIFICATION_KEYS)[number];

/** Ordered staff nav routes + which ones are turned off for a scope. */
export interface SidebarNavConfig {
  order: string[];
  /** Route paths hidden from the sidebar and blocked for direct URL access. */
  disabled: string[];
}

export interface SidebarOrderSettings {
  globalDefault: SidebarNavConfig;
  /** title -> its override, or null if it has none (inherits global default) */
  titleOverrides: Record<string, SidebarNavConfig | null>;
}

/**
 * Superusers (`profiles.is_superuser`) bypass the title/override system and
 * receive every capability. Everyone else is title + per-person override.
 *
 * Barcode edits (`edit_barcode`) and PO number (`edit_po_number`) are normal
 * capabilities. Permanent delete is superuser-only (`requireSuperuser`), not a
 * catalog flag.
 */
export interface EffectivePermissions {
  role: string | null;
  title: string | null;
  isSuperuser: boolean;
  permissions: Record<CapabilityKey, boolean>;
}

/** Dashboard must stay reachable so staff cannot lock themselves out of the app. */
export const SIDEBAR_ALWAYS_ENABLED = ['/staff/dashboard'] as const;

export function isSidebarRouteAlwaysEnabled(to: string): boolean {
  return (SIDEBAR_ALWAYS_ENABLED as readonly string[]).includes(to);
}

/** True when pathname is the nav route or a nested path under it (not a sibling prefix). */
export function pathnameMatchesNavRoute(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function emptyNavConfig(): SidebarNavConfig {
  return { order: [], disabled: [] };
}

/** Accepts legacy string[] or `{ order, disabled }` from app_settings / staff_titles. */
export function parseSidebarNav(raw: unknown): SidebarNavConfig {
  if (raw == null) return emptyNavConfig();
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return emptyNavConfig();
    }
  }
  if (Array.isArray(value)) {
    return {
      order: value.filter((v): v is string => typeof v === 'string'),
      disabled: [],
    };
  }
  if (value && typeof value === 'object') {
    const obj = value as { order?: unknown; disabled?: unknown };
    const order = Array.isArray(obj.order)
      ? obj.order.filter((v): v is string => typeof v === 'string')
      : [];
    const disabled = Array.isArray(obj.disabled)
      ? obj.disabled.filter(
          (v): v is string => typeof v === 'string' && !isSidebarRouteAlwaysEnabled(v)
        )
      : [];
    return { order, disabled };
  }
  return emptyNavConfig();
}

/**
 * Title override wins when it is set. NULL and a legacy empty array inherit
 * the global default (same rule as getMySidebarNav).
 */
export function resolveEffectiveSidebarNav(titleRaw: unknown, globalRaw: unknown): SidebarNavConfig {
  if (titleRaw != null && !(Array.isArray(titleRaw) && titleRaw.length === 0)) {
    return parseSidebarNav(titleRaw);
  }
  return parseSidebarNav(globalRaw);
}

/** Titles a staff access request may pick. Superuser / manager titles are not on this list. */
export const REQUESTABLE_STAFF_TITLES = ['Equipment Team', 'Lab Assistant', 'Production'] as const;
export type RequestableStaffTitle = (typeof REQUESTABLE_STAFF_TITLES)[number];

export function isRequestableStaffTitle(value: string | null | undefined): value is RequestableStaffTitle {
  return !!value && (REQUESTABLE_STAFF_TITLES as readonly string[]).includes(value);
}
